import React, { useState } from 'react';

import { formatDate } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Card, H2, Link, Separator, Text } from '@betterment/samba-react';
import { CursorProvider } from '@betterment/shared-components';

import { ComparisonDropdown } from './benchmark-comparison/ComparisonDropdown';
import { BenchmarkHistoryDocument } from './benchmark-comparison/graphql/benchmarkHistory.generated';
import { BenchmarksDocument } from './benchmark-comparison/graphql/benchmarks.generated';
import { TimeWeightedReturnsHistoryDocument } from './graphql/timeWeightedReturnsHistory.generated';
import { TimeWeightedReturnsChart } from './TimeWeightedReturnsChart';
import { TimeWeightedReturnsChartData } from './TimeWeightedReturnsChartData';

import { DateRangeOption } from '../../../../graphql/schema';

export const TimeWeightedReturns: React.FC<{
  dateRangeOption: DateRangeOption;
  accountId: string;
}> = ({ dateRangeOption, accountId }) => {
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useState<string | null>(null);

  const { data, loading } = useGraphQLQuery(TimeWeightedReturnsHistoryDocument, {
    variables: { dateRangeOption, accountIds: [accountId] },
    throwOnError: true,
  });

  const { data: benchmarksData } = useGraphQLQuery(BenchmarksDocument, {
    throwOnError: true,
  });

  const selectedBenchmarkIds = () => (selectedBenchmarkId ? [selectedBenchmarkId] : []);
  const { data: benchmarkHistoryData } = useGraphQLQuery(BenchmarkHistoryDocument, {
    variables: { dateRangeOption, benchmarkIds: selectedBenchmarkIds() },
    throwOnError: true,
  });

  const timeSeries = data?.performance.timeWeightedReturns.timeSeries;
  const account = data?.performance.accounts[0];
  const portfolioDescription =
    account?.__typename === 'ManagedAccount' ? account.portfolio.configurationDescription : undefined;

  const benchmarks = benchmarksData?.performance.benchmarks ?? [];
  const benchmarkTimeSeries = benchmarkHistoryData?.performance.benchmarks[0]?.timeWeightedReturns?.timeSeries;
  const benchmarkName = benchmarkHistoryData?.performance.benchmarks[0]?.name;

  const endDate = timeSeries && timeSeries.length > 0 ? timeSeries[timeSeries.length - 1].date : undefined;

  return (
    <Card>
      <Card.Header css={{ padding: 'space.300', display: 'flex', justifyContent: 'space-between' }}>
        <H2 variant="title200">Time-weighted return</H2>
        <ComparisonDropdown
          benchmarks={benchmarks}
          selectedBenchmarkId={selectedBenchmarkId}
          onBenchmarkChange={setSelectedBenchmarkId}
        />
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content css={{ padding: 'space.300' }}>
        <CursorProvider>
          <TimeWeightedReturnsChartData
            timeSeries={timeSeries}
            benchmarkTimeSeries={benchmarkTimeSeries}
            benchmarkName={benchmarkName}
            loading={loading}
            portfolioDescription={portfolioDescription}
          />
          <TimeWeightedReturnsChart timeSeries={timeSeries} benchmarkTimeSeries={benchmarkTimeSeries} />
        </CursorProvider>
        {endDate && (
          <Text variant="text50" color="text.weak">
            {`As of ${formatDate(endDate, 'longOrdinalShortMonth')}`}.{' '}
            <Link
              variant="textSecondary"
              href="https://www.betterment.com/help/internal-rate-of-return"
              target="_blank"
              rel="noopener noreferrer"
            >
              Disclosures
            </Link>
          </Text>
        )}
      </Card.Content>
    </Card>
  );
};
